import { React, Styled } from '@common/3rd';
import { DeskComponentRegistry, ModelWidget } from '@component';
import { DeskToggleComponents, DeskToggleProps } from '@my-types';

/**
 * 标准桌面开关按钮
 */
class DeskToggle<P extends DeskToggleProps, S, C extends DeskToggleComponents> extends ModelWidget<P, S, C> {
	toggleRef: React.RefObject<any>;

	constructor(props: any, context?: any) {
		super(props, context);
		this.toggleRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-toggle',
				tabIndex: 0
			})`
				padding: ${props => props.theme.TOGGLE_VERTICAL_PADDING}px 0;
				height: ${props => props.theme.TOGGLE_HEIGHT}px;
				width: ${props => props.theme.TOGGLE_WIDTH}px;
				outline: none;
				&[data-value=true] > div > span {
					left: calc(100% - ${props => props.theme.TOGGLE_SLIDER_WIDTH + 1}px);
				}
				&[data-value=true]:not([data-readonly=true]) {
					> div {
						border-color: ${props => props.theme.TOGGLE_TRUE_BORDER_COLOR};
						background-color: ${props => props.theme.TOGGLE_TRUE_BACKGROUND_COLOR};
						> span {
							background-color: ${props => props.theme.TOGGLE_TRUE_SLIDER_COLOR};
						}
					}
				}
				${props => this.renderCSS(props.theme)}
			`,
			InnerBox: Styled.div.attrs({
				'data-widget': 'desk-toggle-innerbox'
			})`
				position: relative;
				width: 100%;
				height: ${props => props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2}px;
				border-radius: ${props => (props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2) / 2}px;
				border: ${props => props.theme.BORDER};
				border-color: ${props => props.theme.TOGGLE_BORDER_COLOR};
				background-color: ${props => props.theme.TOGGLE_BACKGROUND_COLOR};
				cursor: pointer;
				transition: background-color ease-in-out 300ms, border-color ease-in-out 300ms;

				&:before, &:after {
					display: inline-block;
					position: absolute;
					text-align: center;
					-moz-osx-font-smoothing: grayscale;
					-webkit-font-smoothing: antialiased;
					font-style: normal;
					-webkit-font-feature-settings: normal;
					font-feature-settings: normal;
					font-variant: normal;
					text-rendering: auto;
					width: 1.25em;
					font-family: 'Font Awesome 5 Free';
					font-weight: 900;
					width: 12px;
					height: ${props => props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2}px;
					line-height: ${props => props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2}px;
					font-size: ${props => props.theme.FONT_SIZE_SMALL}px;
					color: ${props => props.theme.COLOR_REVERSED};
				}
				&:before {
					content: '\f00c';
					left: 5px;
				}
				&:after {
					content: '\f00d';
					right: 5px;
					z-index: -1;
				}
			`,
			Slider: Styled.span.attrs({
				'data-widget': 'desk-toggle-slider'
			})`
				display: block;
				position: absolute;
				height: ${props => props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2 - 4}px;
				width: ${props => props.theme.TOGGLE_SLIDER_WIDTH}px;
				top: 1px;
				left: 1px;
				border-radius: ${props => (props.theme.TOGGLE_HEIGHT - props.theme.TOGGLE_VERTICAL_PADDING * 2 - 4) / 2}px;
				background-color: ${props => props.theme.TOGGLE_SLIDER_COLOR};
				transition: left ease-in-out 300ms;
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box
				ref={this.toggleRef}
				onFocus={this.onInputFocus}
				onBlur={this.onInputBlur}
				onClick={this.onClicked}
				data-value={this.getValueFromModel() ? 'true' : undefined}
				data-readonly={!this.isEditable()}
			>
				<C.InnerBox>
					<C.Slider />
				</C.InnerBox>
			</C.Box>
		);
	}
	protected setValueToDOM(value: any, propName?: string): this {
		this.forceUpdate();
		return this;
	}
	protected isEditable(): boolean {
		const props = this.props as any;
		return !(props.readonly || props.disabled);
	}
	private onClicked = (): void => {
		if (!this.isEditable()) {
			return;
		}
		const value = this.getValueFromModel();
		this.setValueToModel(!!!value);
	};
	private onInputFocus = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	private onInputBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
	focus(): this {
		this.toggleRef.current.focus();
		return this;
	}
}

DeskComponentRegistry.register({
	toggle: DeskToggle
});

export { DeskToggle };
